'use client'

import { useState, useEffect } from 'react'
import { ROLES, type Role } from '@/lib/constants'

interface User {
  id: string
  username: string
  email?: string
}

interface UserPermissions {
  userId: string
  username: string
  roles: Role[]
  isRootAdmin: boolean
  isSiteAdmin: boolean
  isNewsEditor: boolean
  isServerAdmin: boolean
  isCommitteeEditor: boolean
  isDocsMaintainer: boolean
  isReadOnlyStaff: boolean
  hasAnyAdminRole: boolean
  serverAdminFor: string[]
  committeeEditorFor: string[]
}

interface AuthState {
  user: User | null
  permissions: UserPermissions | null
  loading: boolean
  error: string | null
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    permissions: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/me', {
          method: 'GET',
          credentials: 'include',
        })

        if (response.ok) {
          const data = await response.json()
          if (data.success) {
            setAuthState({
              user: data.data.user,
              permissions: data.data.permissions,
              loading: false,
              error: null,
            })
          } else {
            setAuthState({
              user: null,
              permissions: null,
              loading: false,
              error: null,
            })
          }
        } else {
          setAuthState({
            user: null,
            permissions: null,
            loading: false,
            error: null,
          })
        }
      } catch (error) {
        setAuthState({
          user: null,
          permissions: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Auth check failed',
        })
      }
    }

    checkAuth()
  }, [])

  const hasRole = (role: Role): boolean => {
    return authState.permissions?.roles.includes(role) ?? false
  }

  const hasAnyAdminRole = (): boolean => {
    if (!authState.permissions) return false
    
    return authState.permissions.isSiteAdmin || 
           authState.permissions.isNewsEditor || 
           authState.permissions.isServerAdmin || 
           authState.permissions.isCommitteeEditor || 
           authState.permissions.isDocsMaintainer ||
           authState.permissions.isReadOnlyStaff
  }

  return {
    user: authState.user,
    permissions: authState.permissions,
    loading: authState.loading,
    error: authState.error,
    isAuthenticated: !!authState.user,
    hasRole,
    hasAnyAdminRole,
  }
}